<template>
  <div class="edit-user-container">
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>
    <template v-if="user">
      <b-notification
        v-if="user.isPendingApproval"
        class="pending-approval-notification"
        type="is-info"
        :closable="false"
      >
        {{ $t('edit_user_profile.pending_approval_message') }}
      </b-notification>
      <b-notification
        v-if="user.isRejected"
        class="pending-approval-notification"
        type="is-info"
        :closable="false"
      >
        {{ $t('edit_user_profile.rejected_message') }}
      </b-notification>

      <h4>{{ $t('edit_user_profile.title') }}</h4>

      <div class="edit-user-form-container">
        <div class="column">
          <div class="row">
            <img class="avatar" src="/img/icons/user.png" />
          </div>
          <b-field :label="$t('edit_user_profile.full_name')">
            <b-input v-model="user.fullname"></b-input>
          </b-field>
          <b-field :label="$t('edit_user_profile.email')">
            <b-input v-model="user._id"></b-input>
          </b-field>
          <b-field :label="$t('edit_user_profile.primary_location')">
            <b-select
              v-model="user.location"
              :placeholder="userLocationName"
              @input="updateLocation"
            >
              <option v-for="site in sites" :value="site._id" :key="site._id">
                {{ site.siteName }}
              </option>
            </b-select>
          </b-field>
          <b-field :label="$t('edit_user_profile.secondary_locations')">
            <b-taginput
              v-model="user.secondaryLocations"
              :data="filteredSites"
              field="siteName"
              autocomplete
              :allow-new="false"
              :open-on-focus="true"
              @typing="getFilteredSitesTags"
            >
            </b-taginput>
          </b-field>
          <b-field :label="$t('edit_user_profile.phone_number')">
            <b-input v-model="user.phone"></b-input>
          </b-field>
        </div>

        <div class="column">
          <div class="row img-space"></div>
          <b-field :label="$t('edit_user_profile.role')">
            <b-select
              v-model="newUserRole"
              :placeholder="
                parseInt(this.user.privil, 10) === 1
                  ? $t('edit_user_profile.power_admin_role')
                  : this.user.role
                  ? this.user.role
                  : $t('edit_user_profile.placeholder_role')
              "
            >
              <option v-if="privil" :value="'power'">
                {{ $t('edit_user_profile.power_admin_role') }}
              </option>
              <option v-for="role in roles" :value="role._id" :key="role._id">
                {{ role.rolename }}
              </option>
            </b-select>
          </b-field>

          <label class="label">{{ $t('edit_user_profile.ownership') }}</label>
          <div class="ownership">
            <b-loading
              :is-full-page="false"
              :active="ownershipLoading"
            ></b-loading>
            <div v-if="!ownershipLoading">
              <p class="ownership-help">
                Transfer ownership of this user's templates and jobs to another
                user.
              </p>
              <p>
                {{
                  $t('edit_user_profile.user_owned_template_count', {
                    count: userOwnedTemplatesCount,
                  })
                }}
              </p>
              <p>
                {{
                  $t('edit_user_profile.user_owned_job_count', {
                    count: userOwnedJobsCount,
                  })
                }}
              </p>

              <b-button
                @click="goToOwnershipReassignPage"
                type="is-primary"
                :disabled="!hasOwnedItems"
              >
                Reassign Ownership (Templates/Jobs)
              </b-button>
            </div>
            <div v-else class="ownership-loading">
              <p>Loading ownership details...</p>
            </div>
          </div>

          <b-field :label="$t('edit_user_profile.additional_permissions')">
            <b-checkbox v-model="user.engineerReview">{{
              $t('edit_user_profile.enginner_review_permission')
            }}</b-checkbox>
          </b-field>
        </div>
      </div>

      <div class="actions">
        <template v-if="user.isPendingApproval || user.isRejected">
          <b-button @click="handleApproveUser" type="is-success">{{
            $t('edit_user_profile.approve_button')
          }}</b-button>
          <b-button
            v-if="!user.isRejected"
            @click="handleRejectUser"
            type="is-danger"
            >{{ $t('edit_user_profile.reject_button') }}</b-button
          >
        </template>
        <template v-else>
          <b-button @click="deleteUser" type="is-danger">{{
            $t('edit_user_profile.delete_button')
          }}</b-button>
          <b-button @click="updateUserProfile" type="is-primary">{{
            $t('edit_user_profile.update_button')
          }}</b-button>
        </template>
        <b-button @click="handleClose">{{
          $t('edit_user_profile.cancel_button')
        }}</b-button>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'edit-user-profile',
  data() {
    return {
      isNewPasswordActive: false,
      newUserRole: null,
      userLocationName: '',
      privil: false,
      isLoading: true,
      user: null,
      filteredSites: null,
      ownershipLoading: true,
    };
  },
  computed: {
    sites() {
      return this.$store.state.sites;
    },
    sitesWithoutPrimaryLocation() {
      const sitesWithoutPrimaryLocation = [...this.sites];
      return sitesWithoutPrimaryLocation.filter(
        (site) => site._id !== this.user.location
      );
    },
    roles() {
      if (this.currentUser && parseInt(this.currentUser.privil, 10) === 1) {
        return this.$store.state.userRoles;
      }
      return this.$store.state.userRoles.filter(
        (role) => role.rolename.toLowerCase() !== 'power admin'
      );
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    userOwnedTemplatesCount() {
      return this.$store.state.userOwnedTemplatesCount;
    },
    userOwnedJobsCount() {
      return this.$store.state.userOwnedJobsCount;
    },
    hasOwnedItems() {
      return this.userOwnedTemplatesCount > 0 || this.userOwnedJobsCount > 0;
    },
    userLocationNames() {
      const secondaryLocations = (this.user.secondaryLocations || []).map(
        (location) => location._id
      );
      return [this.user.location, ...secondaryLocations].map(
        (location) => this.sites.find((site) => site._id === location).siteName
      );
    },
  },

  watch: {
    newUserRole(newValue) {
      if (newValue === 'power') {
        this.user.role = 'admin';
        this.user.privil = 1;
      } else {
        const chosenRole = this.roles.find((role) => role._id === newValue);
        this.user.role = chosenRole.rolename;
        this.user.privil = 0;
      }
    },
  },

  beforeRouteEnter(to, {}, next) {
    next();
  },

  async created() {
    await this.initializeUserProfile();
  },

  async beforeRouteUpdate(to, from, next) {
    if (to.params.id === from.params.id) {
      next();
      return;
    }

    await this.initializeUserProfile(to.params.id, to.params?.user);
    next();
  },

  methods: {
    async initializeUserProfile(routeId, selectedUser) {
      this.isLoading = true;
      this.ownershipLoading = true;
      const id = routeId || this.$route.params.id;
      const selected = selectedUser || this.$route.params?.user;

      try {
        if (selected && selected._id === id) {
          this.$store.commit('SET_USER', selected);
        } else {
          await this.$store.dispatch('getUserProfile', id);
        }

        const requests = [
          this.$store.dispatch('getUserRoles'),
          this.$store.dispatch('countUserOwnedTemplates', id),
          this.$store.dispatch('countUserOwnedJobs', id),
        ];

        if (this.$store.state.sites.length === 0) {
          requests.push(this.$store.dispatch('getSites'));
        }

        await Promise.all(requests);

        this.user = { ...this.$store.state.user };
        if (this.user.secondaryLocations) {
          this.user.secondaryLocations = this.user.secondaryLocations
            .map((locationEntry) => {
              if (locationEntry && typeof locationEntry === 'object') {
                return locationEntry;
              }
              return this.sites.find((site) => site._id === locationEntry);
            })
            .filter(Boolean);
        }

        if (this.currentUser) {
          this.privil = Boolean(parseInt(this.currentUser.privil, 10));
        }

        this.updateLocation();
        this.filteredSites = this.sitesWithoutPrimaryLocation;
      } finally {
        this.ownershipLoading = false;
        this.isLoading = false;
      }
    },

    updateLocation() {
      if (this.user.secondaryLocations) {
        this.user.secondaryLocations = this.user.secondaryLocations.filter(
          (location) => location._id !== this.user.location
        );
      }
      const site = this.sites.find(
        (siteToFind) => siteToFind._id === this.user.location
      );
      this.userLocationName = site ? site.locationName : '';
    },

    handleClose() {
      this.$router.push({
        name: 'admin-root',
      });
    },

    goToOwnershipReassignPage() {
      this.$router.push({
        name: 'edit-user-jobownership',
        params: { id: this.user._id },
      });
    },

    async updateUserProfile() {
      const userToUpdate = { ...this.user };
      if (userToUpdate.secondaryLocations) {
        userToUpdate.secondaryLocations = this.user.secondaryLocations.map(
          (location) => location._id
        );
      }

      await this.$store.dispatch('updateUser', userToUpdate);

      this.$store.dispatch(
        'setSuccessMessage',
        this.$t('edit_user_profile.update_success_message')
      );

      this.$router.push({
        name: 'admin-root',
      });
    },

    async deleteUser() {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this user?',
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        confirmText: 'Delete',
        onConfirm: async () => {
          this.isLoading = true;
          await this.$store.dispatch('deleteUser', this.user);

          this.$store.dispatch(
            'setSuccessMessage',
            this.$t('edit_user_profile.delete_success_message')
          );

          setTimeout(() => {
            this.$router.push({
              name: 'admin-root',
            });
          }, 3000);
        },
      });
    },

    async handleApproveUser() {
      const name = this.user.fullname;
      const { role } = this.user;
      const locations = this.userLocationNames;
      this.$buefy.dialog.confirm({
        message: this.$t('edit_user_profile.approve_question', {
          name,
          role,
          locations,
        }),
        type: 'is-success',
        hasIcon: true,
        icon: 'check-decagram',
        confirmText: this.$t('edit_user_profile.approve_button'),
        onConfirm: async () => {
          this.isLoading = true;

          const userToUpdate = { ...this.user };
          if (userToUpdate.secondaryLocations) {
            userToUpdate.secondaryLocations = this.user.secondaryLocations.map(
              (location) => location._id
            );
          }

          await this.$store.dispatch('approveUser', userToUpdate);

          this.$store.dispatch(
            'setSuccessMessage',
            this.$t('edit_user_profile.approve_success_message')
          );

          setTimeout(() => {
            this.$router.push({
              name: 'admin-root',
            });
          }, 3000);
        },
      });
    },

    async handleRejectUser() {
      const name = this.user.fullname;
      this.$buefy.dialog.confirm({
        message: this.$t('edit_user_profile.reject_question', { name }),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        confirmText: this.$t('edit_user_profile.reject_button'),
        onConfirm: async () => {
          this.isLoading = true;
          await this.$store.dispatch('rejectUser', this.user);

          this.$store.dispatch(
            'setSuccessMessage',
            this.$t('edit_user_profile.reject_success_message')
          );

          setTimeout(() => {
            this.$router.push({
              name: 'admin-root',
            });
          }, 3000);
        },
      });
    },

    getFilteredSitesTags(text) {
      this.filteredSites = this.sitesWithoutPrimaryLocation.filter(
        (location) => {
          return (
            location.siteName
              .toString()
              .toLowerCase()
              .indexOf(text.toLowerCase()) >= 0
          );
        }
      );
    },
  },
};
</script>

<style lang="scss">
.img-space {
  margin-top: 142px;
}
.avatar {
  width: 80px;
  height: 80px;
  margin: 30px 0;
  margin-left: 8px;
  border-radius: 80px;
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  background-color: rgba(37, 48, 92, 0.3);
}

.edit-user-container {
  h4 {
    margin-left: 5%;
    margin-top: 2%;
    font-weight: bold;
    color: #333333;
    font-size: 20px;
  }
}

.edit-user-form-container {
  display: flex;
  flex-direction: row;
  width: 75%;
  margin: auto;
}

.first-column {
  display: flex;
  width: 50%;
  flex-direction: column;
}

.column {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  flex-direction: row;
}
select,
.select,
.is-empty {
  width: 100%;
}

.actions {
  display: flex;
  gap: 10px;
  right: 0;
  bottom: 0;
  position: absolute;
  margin-right: 10%;
  margin-bottom: 5%;
}

.ownership {
  position: relative;
  background-color: rgba(255, 255, 255, 1);

  .ownership-help {
    margin-bottom: 8px;
  }

  .ownership-empty {
    margin-top: 8px;
  }
}

.notification.pending-approval-notification {
  padding-top: 10px;
  padding-bottom: 10px;
  margin: 0;
}
</style>
